/**
 * The donor's checkout (H3).
 *
 * A state machine over `./flow.ts` rather than a form that posts to a URL: the
 * card path is seven server round trips and two iframes deep, and a form
 * navigation cannot express "wait for a trusted message, then capture".
 *
 * Nothing on this page computes money. The amount the donor types is sent as
 * `chosenAmountCents` and everything else — gross, fee, seats, currency — comes
 * back from the server's snapshot. The bank path is the two steps it can honestly
 * be: create the donation, then show the administrator-managed details.
 */

import { ApiRequestError, apiFetch } from '@qalam/shared/frontend';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PaymentClient } from '../checkout/api';
import DdcFrame, { createDeviceDataGate, type DeviceDataGate } from '../checkout/DdcFrame';
import { debugDump, debugTimer } from '../checkout/debugLog';
import {
  type CheckoutState,
  challengeWatchdogMs,
  INITIAL_STATE,
  pay,
  run,
  stillWaitingMs,
} from '../checkout/flow';
import type { ThreeDsMessage } from '../checkout/messages';
import SuccessScreen from '../checkout/SuccessScreen';
import ThreeDSChallenge from '../checkout/ThreeDSChallenge';
import { UC_CONTAINER_CSS, UC_CONTAINERS, unifiedCheckout } from '../checkout/uc';

export interface BankDetails {
  accountHolder: string;
  bankName: string;
  iban: string;
  currency: string;
  instructionsAr: string;
}

export interface CheckoutPageProps {
  /** Injected so a test drives the real component against a fake client. */
  client: PaymentClient;
  /** Defaults to the real adapter; H6 substitutes only this. */
  adapter?: typeof unifiedCheckout;
  /** Read the bank settings from the server; injected for tests. */
  loadBankDetails?: () => Promise<BankDetails>;
  /** Carried from the packages page; a hint, never a price. */
  seats?: number;
  /**
   * The device-data-collection hand-off.
   *
   * Injectable for the same reason `adapter` is: the real gate waits up to five
   * seconds for Cardinal, and a test that is not testing Cardinal should not
   * spend five seconds proving that it is patient.
   */
  deviceDataGate?: DeviceDataGate;
}

const defaultLoadBankDetails = (): Promise<BankDetails> =>
  apiFetch<BankDetails>('/api/public/bank-transfer-instructions');

/**
 * The source and the marketing parameters the link carried.
 *
 * Read from the URL, and only the known keys: a query string can carry
 * anything, and this ends up in a report.
 */
function readAttribution(): { source?: string; utm?: Record<string, string> } {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
    const value = params.get(key);
    if (value && value.trim().length > 0) utm[key] = value.trim().slice(0, 64);
  }
  const ref = params.get('ref');
  const source = (utm.utm_source ?? ref ?? '').trim().slice(0, 64);
  return {
    ...(source.length > 0 ? { source } : {}),
    ...(Object.keys(utm).length > 0 ? { utm } : {}),
  };
}

export default function CheckoutPage({
  client,
  adapter = unifiedCheckout,
  loadBankDetails = defaultLoadBankDetails,
  seats = 0,
  deviceDataGate,
}: CheckoutPageProps) {
  const { t } = useTranslation();
  // The campaign the donor came for, from the link: `/give?campaign=<slug>`.
  // The slug is sent as it is; the server resolves it and refuses a slug that
  // names no open campaign.
  //
  // Read from the URL rather than from a router hook, so this page still
  // renders in a test with no router around it.
  const campaignSlug =
    typeof window === 'undefined'
      ? undefined
      : (new URLSearchParams(window.location.search).get('campaign') ?? undefined);
  // Where the donor came from, as the link said. The server records it; the
  // browser never decides what it is.
  const attribution = readAttribution();
  const [state, setState] = useState<CheckoutState>(INITIAL_STATE);
  // Deliberately empty. The seat count from the packages page cannot be turned
  // into an amount here: the seat price is an admin-editable server setting and
  // this page has no route that could read it. A prefilled number would be a
  // hardcoded business parameter that can disagree with the receipt (rule 7).
  const [amountText, setAmountText] = useState('');
  const [coverFees, setCoverFees] = useState(false);
  const [method, setMethod] = useState<'CARD' | 'BANK_TRANSFER'>('CARD');
  const [guestEmail, setGuestEmail] = useState('');
  const [bank, setBank] = useState<BankDetails | null>(null);
  const [bankUnavailable, setBankUnavailable] = useState(false);
  const [bankPending, setBankPending] = useState(false);
  // The last completed run, so a retry after a decline re-uses the same
  // donation rather than stranding it as PENDING forever.
  const lastRun = useRef<CheckoutState>(INITIAL_STATE);
  // One gate per mount: the DDC frame reports into it and the flow waits on it,
  // so enrollment does not race the collector.
  const ownGate = useMemo(() => createDeviceDataGate(), []);
  const gate = deviceDataGate ?? ownGate;

  const amountCents = Math.round(Number.parseFloat(amountText || '0') * 100);
  const amountIsValid = Number.isSafeInteger(amountCents) && amountCents > 0;
  const busy =
    state.stage === 'creating' ||
    state.stage === 'card' ||
    state.stage === 'authenticating' ||
    state.stage === 'challenging' ||
    state.stage === 'paying';

  const startCard = useCallback(async () => {
    // A retry re-uses the donation it already created.
    //
    // The amount, the fee and the seat count are frozen on that row, and
    // re-running `pay` on it is what `auth-setup` turns into a fresh attempt.
    // Creating a second donation would strand the first as PENDING forever.
    //
    // A card frame that never answered is exactly this case: the donation
    // exists, no money moved, and the donor is being offered another go at
    // the same amount. It is not the same as a fresh checkout, and it must not
    // become one. The challenge watchdog lands here too: the challenge never
    // confirmed or denied anything, so the donation stands and the retry is a
    // new attempt on it.
    const reusable =
      state.stage === 'declined' ||
      state.errorCode === 'show_timeout' ||
      state.errorCode === 'challenge_timeout'
        ? lastRun.current
        : undefined;
    const next = await run(
      { client, adapter, deviceData: gate },
      {
        chosenAmountCents: amountCents,
        coverFees,
        method: 'CARD',
        guestEmail: guestEmail.trim() || undefined,
        ...(campaignSlug ? { campaignSlug } : {}),
        ...(attribution.source ? { source: attribution.source } : {}),
        ...(attribution.utm ? { utm: attribution.utm } : {}),
      },
      setState,
      reusable,
    );
    lastRun.current = next;
  }, [
    adapter,
    amountCents,
    client,
    coverFees,
    gate,
    guestEmail,
    state.stage,
    state.errorCode,
    campaignSlug,
    attribution.source,
    attribution.utm,
  ]);

  const startBank = useCallback(async () => {
    setState({ ...INITIAL_STATE, stage: 'creating' });
    try {
      const created = await client.createDonation({
        chosenAmountCents: amountCents,
        method: 'BANK_TRANSFER',
        // A bank transfer has no gateway fee to cover; the server forces it
        // false anyway, and sending it would be claiming a choice that does not
        // exist.
        coverFees: false,
        idempotencyKey: `web-bank-${Date.now()}`,
        guestEmail: guestEmail.trim() || undefined,
        ...(campaignSlug ? { campaignSlug } : {}),
        ...(attribution.source ? { source: attribution.source } : {}),
        ...(attribution.utm ? { utm: attribution.utm } : {}),
      });
      setState({
        stage: 'creating',
        donationId: created.donationId,
        guestToken: created.guestToken,
      });
      setBankPending(true);
      const details = await loadBankDetails();
      setBank(details);
      setBankPending(false);
    } catch (error) {
      setBankPending(false);
      // The API answers `bank_not_configured` with 503 when the administrator
      // has not filled the bank settings in. That is the one case where the
      // method must disappear rather than be offered and then fail.
      if (error instanceof ApiRequestError && error.code === 'bank_not_configured') {
        setBankUnavailable(true);
      }
      setState({ stage: 'failed', errorCode: 'bank_unavailable' });
    }
  }, [
    amountCents,
    client,
    guestEmail,
    loadBankDetails,
    campaignSlug,
    attribution.utm,
    attribution.source,
  ]);

  const onChallengeMessage = useCallback(
    (message: ThreeDsMessage) => {
      if (message.type === '3ds-failed') {
        // The authentication itself failed. Showing "pending" here would tell a
        // donor to wait for a payment that is never coming.
        setState((current) => ({ ...current, stage: 'failed', errorCode: 'three_ds_failed' }));
        return;
      }
      void pay({ client, adapter }, { ...state }, setState);
    },
    [adapter, client, state],
  );

  // The challenge watchdog.
  //
  // `status=failed` arrives through the frame, and a frame whose redirect
  // failed, whose issuer page never loaded, or which the donor simply closed
  // delivers nothing at all. Neither case may mean "loading forever": the timer
  // ends the wait, says plainly that nothing was charged, and offers a retry
  // that starts a new attempt on the donation that already exists. A trusted
  // message that races the timer changes the stage first, and the guard keeps
  // the timer from overwriting an answer that already landed.
  useEffect(() => {
    if (state.stage !== 'challenging') return;
    const timer = setTimeout(() => {
      debugTimer('watchdog', 'fired');
      setState((current) =>
        current.stage === 'challenging'
          ? { ...current, stage: 'failed', errorCode: 'challenge_timeout' }
          : current,
      );
    }, challengeWatchdogMs());
    return () => {
      debugTimer('watchdog', 'cleared');
      clearTimeout(timer);
    };
  }, [state.stage]);

  // The still-waiting hint, on every waiting state.
  //
  // `busy` is exactly the set of stages where the donor has done their part and
  // the page has not answered. After `stillWaitingMs()` in any of them, the page
  // says so - in words, with a way to hand a person the facts - because a
  // spinner with no sentence is how a stall looks like a working system. The
  // hint changes no state and cancels nothing: the flow's own timeouts and
  // watchdogs still own every outcome.
  const [waitingTooLong, setWaitingTooLong] = useState(false);
  const [diagnosticsCopied, setDiagnosticsCopied] = useState(false);
  useEffect(() => {
    if (!busy) {
      setWaitingTooLong(false);
      return;
    }
    debugTimer('still-waiting', 'started');
    const timer = setTimeout(() => {
      debugTimer('still-waiting', 'fired');
      setWaitingTooLong(true);
    }, stillWaitingMs());
    return () => {
      debugTimer('still-waiting', 'cleared');
      clearTimeout(timer);
    };
  }, [busy]);

  const copyDiagnostics = useCallback(() => {
    const text = debugDump();
    void navigator.clipboard
      ?.writeText(text)
      .then(() => setDiagnosticsCopied(true))
      .catch(() => setDiagnosticsCopied(false));
  }, []);

  return (
    <main data-testid="checkout">
      <h1>{t('checkout.title')}</h1>

      {seats > 0 ? <p data-testid="selected-seats">{`${t('checkout.seats')}: ${seats}`}</p> : null}

      {state.stage === 'success' ? (
        <SuccessScreen seats={state.seats ?? 0} receiptNumber={state.receiptNumber} />
      ) : (
        <>
          <label htmlFor="amount">
            {t('checkout.customAmount')}
            <input
              id="amount"
              type="number"
              inputMode="decimal"
              min="1"
              step="0.01"
              value={amountText}
              placeholder={t('checkout.amountHint')}
              onChange={(event) => setAmountText(event.target.value)}
            />
          </label>

          <label htmlFor="guest-email">
            {t('checkout.guestEmail')}
            <input
              id="guest-email"
              type="email"
              value={guestEmail}
              onChange={(event) => setGuestEmail(event.target.value)}
            />
          </label>

          <label htmlFor="cover-fees">
            <input
              id="cover-fees"
              type="checkbox"
              checked={coverFees}
              disabled={method === 'BANK_TRANSFER'}
              onChange={(event) => setCoverFees(event.target.checked)}
            />
            {t('checkout.coverFees')}
          </label>

          <fieldset data-testid="method">
            <legend>{t('checkout.method')}</legend>
            <label htmlFor="method-card">
              <input
                id="method-card"
                data-testid="method-card"
                type="radio"
                name="method"
                checked={method === 'CARD'}
                onChange={() => setMethod('CARD')}
              />
              {t('checkout.card')}
            </label>
            {/* Hidden unless the administrator has configured the bank details,
                or until the server says they have not. Never offered blind. */}
            {!bankUnavailable ? (
              <label htmlFor="method-bank">
                <input
                  id="method-bank"
                  data-testid="method-bank"
                  type="radio"
                  name="method"
                  checked={method === 'BANK_TRANSFER'}
                  onChange={() => setMethod('BANK_TRANSFER')}
                />
                {t('checkout.bank')}
              </label>
            ) : null}
          </fieldset>

          {bankUnavailable ? <p>{t('checkout.bankNotConfigured')}</p> : null}

          <button
            type="button"
            data-testid="pay-now"
            disabled={!amountIsValid || busy}
            onClick={() => (method === 'CARD' ? void startCard() : void startBank())}
          >
            {busy ? t('checkout.working') : t('checkout.payNow')}
          </button>

          {waitingTooLong && busy ? (
            <div data-testid="still-waiting">
              <p>{t('checkout.stillWaiting')}</p>
              <button type="button" data-testid="copy-diagnostics" onClick={copyDiagnostics}>
                {t('checkout.copyDiagnostics')}
              </button>
              {diagnosticsCopied ? (
                <p data-testid="diagnostics-copied">{t('checkout.copied')}</p>
              ) : null}
            </div>
          ) : null}

          {/* `bankPending` only means "still fetching". The instructions stay on
              screen once loaded, because the donation is waiting for a
              confirmation that has not happened yet. */}
          {bankPending && !bank ? <p data-testid="bank-loading">{t('checkout.working')}</p> : null}
          {bank ? (
            <section data-testid="bank-instructions">
              <h2>{t('checkout.bankTitle')}</h2>
              <dl>
                <dt>{t('checkout.bankAccountHolder')}</dt>
                <dd data-testid="bank-account-holder">{bank.accountHolder}</dd>
                <dt>{t('checkout.bankName')}</dt>
                <dd data-testid="bank-name">{bank.bankName}</dd>
                <dt>{t('checkout.bankIban')}</dt>
                <dd data-testid="bank-iban">{bank.iban}</dd>
                <dt>{t('checkout.bankCurrency')}</dt>
                <dd data-testid="bank-currency">{bank.currency}</dd>
              </dl>
              <p>{bank.instructionsAr}</p>
              <p data-testid="bank-pending">{t('checkout.bankWaiting')}</p>
            </section>
          ) : null}

          {state.stage === 'card' ? (
            <p data-testid="stage-card">{t('checkout.cardEntry')}</p>
          ) : null}
          {state.stage === 'authenticating' ? (
            <p data-testid="stage-auth">{t('checkout.verifying')}</p>
          ) : null}

          {/* The two elements Unified Checkout populates. They are rendered
              unconditionally, before any library is loaded, because `show()`
              resolves a selector at the moment it is called and silently renders
              nowhere if the element is absent. This is the difference between a
              donor seeing a payment form and seeing a blank panel. */}
          <style>{UC_CONTAINER_CSS}</style>
          <div className="uc-mount">
            <div id={UC_CONTAINERS.paymentSelection.slice(1)} data-testid="uc-payment-selection" />
            <div id={UC_CONTAINERS.paymentScreen.slice(1)} data-testid="uc-payment-screen" />
          </div>

          {/* The DDC frame exists while the Payer Auth reference is being set up
              and enrolled, and disappears the moment it has been collected. */}
          {state.stage === 'authenticating' && state.deviceDataCollectionUrl && state.ddcOrigin ? (
            <DdcFrame
              deviceDataCollectionUrl={state.deviceDataCollectionUrl}
              ddcOrigin={state.ddcOrigin}
              accessToken={state.accessToken ?? ''}
              onCollected={gate.collected}
              onFailed={() => undefined}
            />
          ) : null}

          {state.stage === 'challenging' && state.stepUpUrl && state.accessToken ? (
            <section data-testid="stage-challenge">
              <p>{t('checkout.challenge')}</p>
              <p>{t('checkout.challengeHint')}</p>
              <ThreeDSChallenge
                stepUpUrl={state.stepUpUrl}
                accessToken={state.accessToken}
                merchantDefinedData=""
                donationId={state.donationId ?? ''}
                onMessage={onChallengeMessage}
              />
            </section>
          ) : null}

          {state.stage === 'declined' ? (
            <section data-testid="stage-declined">
              <p>{t('checkout.retryHint')}</p>
              <button type="button" data-testid="retry" onClick={() => void startCard()}>
                {t('checkout.retry')}
              </button>
            </section>
          ) : null}

          {state.stage === 'declinedByPolicy' ? (
            /*
              No retry button here, on purpose.
              The policy has already looked at this card for this order and
              released it. A "try again" that runs the same card again would ask
              the question that was just answered, and a donor who retries five
              times has learned nothing except that we do not read their answer.
              What the message does instead: say plainly that nothing was taken,
              say that the card is not the problem, and offer the two ways that
              are actually open - another card, or a bank transfer.
            */
            <section data-testid="stage-declined-by-policy">
              <h2>{t('checkout.policyDeclinedTitle')}</h2>
              <p data-testid="policy-declined-no-blame">{t('checkout.policyDeclinedBody')}</p>
              <p>{t('checkout.policyDeclinedNoCharge')}</p>
              <div>
                <button
                  type="button"
                  data-testid="try-another-card"
                  onClick={() => void startCard()}
                >
                  {t('checkout.tryAnotherCard')}
                </button>
                <button type="button" data-testid="choose-bank" onClick={() => void startBank()}>
                  {t('checkout.chooseBankTransfer')}
                </button>
              </div>
            </section>
          ) : null}

          {state.stage === 'failed' && state.errorCode === 'show_timeout' ? (
            /*
              The card frame never answered.
              Its own state, because a donor whose frame timed out has to be
              told two things that the generic failure does not say: that
              nothing was taken, and that trying again is a normal thing to do.
              A reload would throw the donation away and ask for the card
              again; a retry re-uses the donation that was already created.
            */
            <section data-testid="stage-show-timeout">
              <h2>{t('checkout.cardTimeoutTitle')}</h2>
              <p data-testid="card-timeout-reason">
                {t('checkout.errors.show_timeout', { defaultValue: t('common.error') })}
              </p>
              <button
                type="button"
                data-testid="card-timeout-retry"
                onClick={() => void startCard()}
              >
                {t('checkout.retry')}
              </button>
            </section>
          ) : null}

          {state.stage === 'failed' && state.errorCode === 'challenge_timeout' ? (
            /*
              The challenge never said anything, and the watchdog ended the wait.
              Its own screen, because the words a donor needs here are exact:
              their card verification could not be confirmed, nothing was
              charged, and trying again is a normal thing to do. The retry starts
              a new attempt on the donation that already exists — the same
              amount, the same seats, a fresh authentication.
            */
            <section data-testid="stage-challenge-timeout">
              <h2>{t('checkout.challengeTimeoutTitle')}</h2>
              <p data-testid="challenge-timeout-reason">
                {t('checkout.errors.challenge_timeout', { defaultValue: t('common.error') })}
              </p>
              <button
                type="button"
                data-testid="challenge-timeout-retry"
                onClick={() => void startCard()}
              >
                {t('checkout.retry')}
              </button>
            </section>
          ) : null}

          {state.stage === 'failed' &&
          state.errorCode !== 'show_timeout' &&
          state.errorCode !== 'challenge_timeout' ? (
            <section data-testid="stage-failed">
              <h2>{t('checkout.failedTitle')}</h2>
              {/* The specific reason, never a generic apology. A blocked script,
                  a library that never started and a refused `show()` have three
                  different fixes, and only one of them is in the donor's hands;
                  telling all three apart is the whole value of the message. */}
              <p data-testid="failed-reason">
                {t(`checkout.errors.${state.errorCode ?? 'unknown'}`, {
                  defaultValue: t('common.error'),
                })}
              </p>
              <button
                type="button"
                data-testid="failed-reload"
                onClick={() => globalThis.location.reload()}
              >
                {t('checkout.reload')}
              </button>
            </section>
          ) : null}

          {state.stage === 'pending' ? (
            <section data-testid="stage-pending">
              {/* Two different sentences for two different situations, because
                  a donor who is being reviewed should never be told the same
                  thing as a donor whose outcome we have not heard yet. */}
              <h2>{state.inReview ? t('checkout.reviewTitle') : t('checkout.pendingTitle')}</h2>
              <p>{state.inReview ? t('checkout.reviewBody') : t('checkout.pendingBody')}</p>
              {state.inReview ? <p data-testid="review-note">{t('checkout.reviewNote')}</p> : null}
            </section>
          ) : null}
        </>
      )}
    </main>
  );
}
